import priceFormatter from 'currency-formatter'
import { useEffect, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { scrollIntoView } from 'seamless-scroll-polyfill'
import styled from 'styled-components'
import { calcTotalFeePrice } from 'widget/events/utils/calcCharges'
import { DateTime, TimeOnly } from '@sevenrooms/timepiece'
import { HTMLContent } from '@sevenrooms/ui-kit/core'
import { BasicButton } from '@sevenrooms/ui-kit/form'
import { HStack, Box, DividerLine } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { selectInventoryItems, toggleInventoryItem } from '../actions/navigation'
import QuantityStepper from '../containers/QuantityStepper'
import { GUESTLIST_FREE_TYPE, REQUEST_TYPE, RESERVATION_TYPE } from '../reducers/inventoryReducer'
import { getBookingPolicy } from '../selectors/CheckoutPolicySelector'
import { isMultiInventoryEnabled } from '../selectors/eventSelector'
import { hexToRgb } from '../utils/color'
import { parseBoolean } from '../utils/preloadedState'
import { handleHover, handleClick } from './actions'
import { DetailsModal } from './DetailsModal'
import type { Map } from 'immutable'
import type { Dispatch } from 'redux'

priceFormatter.currencies[0].symbol = 'AED'
priceFormatter.currencies[0].symbolOnLeft = true

interface InventoryProps {
  availabilityId: string
  inventoryItem: {
    minQuantity: number
    minPrice: number
    entryPerReservation: number
    inventoryType: string
    inventoryName: string
    inventoryId: string
    maxQuantity: number
    price: number
    compText: string
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inventoryEdits: { [inventoryId: string]: any }
  isHovered: boolean
  toggleInventoryItem: (inventoryId: string, selectedDate: string, selectedEventId: string) => void
  selectInventoryItem: (inventoryId: string, availabilityId: string) => void
  updateSvgLayerClickStatus: (inventoryName: string, selectedDate: string) => void
  onSvgLayerClick: (inventoryName: string, selectedDate: string) => void
  currencyCode: string
  textMinimumSpend: string
  isFeesInPriceDisplayed: boolean
  defaultServiceCharge: number
  selectedDate: string
  selectedEventId: string
  inventoryDesc?: string
  inventoryImageUrls?: string[]
  eventDateFormatted: string
  eventTimeFormatted?: string
  cancellationPolicy?: string
  shouldShowRemainingInventory: boolean
  isMultiInventoryEnabled?: boolean
  isDisabled?: boolean
  currencyFormatter: Intl.NumberFormat
}

const Inventory = ({
  availabilityId,
  inventoryItem,
  inventoryEdits,
  isHovered,
  toggleInventoryItem,
  selectInventoryItem,
  updateSvgLayerClickStatus,
  onSvgLayerClick,
  currencyCode,
  textMinimumSpend,
  isFeesInPriceDisplayed,
  defaultServiceCharge,
  selectedDate,
  selectedEventId,
  inventoryDesc,
  inventoryImageUrls,
  eventDateFormatted,
  eventTimeFormatted,
  cancellationPolicy,
  shouldShowRemainingInventory,
  isMultiInventoryEnabled,
  isDisabled,
  currencyFormatter,
}: InventoryProps) => {
  const dispatch = useDispatch()
  const highlightedInventoryNameRef = useRef<HTMLDivElement>(null)
  const highlightedInventoryRef = useRef<HTMLDivElement>(null)
  const highlightedInventoryButtonRef = useRef<HTMLButtonElement>(null)
  const moreDetailsButtonRef = useRef<HTMLButtonElement>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { price, compText, inventoryId, inventoryType, entryPerReservation } = inventoryItem
  const maxPurchasableQuantity =
    isMultiInventoryEnabled && inventoryType === REQUEST_TYPE
      ? Infinity
      : Math.min(inventoryItem.maxQuantity, inventoryEdits[inventoryId].remainingQuantity)
  const { isHighlighted } = inventoryEdits[inventoryId]
  const quantity = isMultiInventoryEnabled
    ? inventoryEdits[inventoryId].quantity
    : Math.max(inventoryItem.minQuantity, inventoryEdits[inventoryId].quantity)
  const basePrice = price * quantity
  const numGuests = quantity * entryPerReservation

  const totalFeePrice = isMultiInventoryEnabled
    ? calcTotalFeePrice(inventoryItem, 1, price, defaultServiceCharge)
    : calcTotalFeePrice(inventoryItem, quantity, price, defaultServiceCharge)

  let totalPrice
  if (isMultiInventoryEnabled) {
    totalPrice = isFeesInPriceDisplayed ? price + totalFeePrice : price
  } else {
    totalPrice = isFeesInPriceDisplayed ? basePrice + totalFeePrice : basePrice
  }

  const unitPriceFees = calcTotalFeePrice(inventoryItem, 1, price, defaultServiceCharge)

  const getMinDollarText = (quantity: number) => {
    if (!inventoryItem.minPrice || inventoryType !== RESERVATION_TYPE) {
      return ''
    }
    const minDollarAmount = quantity * inventoryItem.minPrice
    const minDollarAmountFormatted = priceFormatter.format(minDollarAmount, {
      code: currencyCode,
    })
    return `${minDollarAmountFormatted} ${textMinimumSpend}`
  }

  const getNumberOfGuestsText = (numOfGuests: number) => {
    if (inventoryType === REQUEST_TYPE) {
      return ''
    }
    if (numOfGuests === 1) {
      return 'For 1 guest'
    }
    return inventoryType === RESERVATION_TYPE ? `Up to ${numOfGuests} guests` : `For ${numOfGuests} guests`
  }

  const headerText = [getNumberOfGuestsText(numGuests), getMinDollarText(quantity)].filter(val => !!val).join(', ')

  const getHeaderText = (
    inventory: { minQuantity: number; minPrice: number; entryPerReservation: number; inventoryType: string },
    quantity: number
  ) => {
    if (inventory.inventoryType === REQUEST_TYPE) {
      return 'Request only'
    }

    let headerText = ''
    const numGuests = quantity === 0 ? inventory.entryPerReservation : quantity * inventory.entryPerReservation

    if (numGuests <= 1) {
      headerText = 'For 1 guest'
    } else {
      headerText = `For ${numGuests} guests`
    }

    if (inventory.minQuantity > 1) {
      headerText = `${headerText}, ${inventory.minQuantity} tickets minimum`
    }

    if (inventory.inventoryType === RESERVATION_TYPE && inventory.minPrice) {
      const minDollarText = (function () {
        const minDollarAmount = quantity === 0 ? inventory.minPrice : quantity * inventory.minPrice
        const minDollarAmountFormatted = currencyFormatter.format(minDollarAmount)
        return `, ${minDollarAmountFormatted} ${textMinimumSpend}`
      })()
      headerText = `${headerText}${minDollarText}`
    }

    return headerText
  }

  let priceText = currencyFormatter.format(totalPrice)
  if (inventoryType === GUESTLIST_FREE_TYPE) {
    priceText = compText
  } else if (inventoryType === REQUEST_TYPE) {
    priceText = 'Request'
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    // Need to wait until the modal is closed before focusing on the button.
    setTimeout(() => {
      moreDetailsButtonRef.current?.focus()
    })
  }

  const unitGuestsText = getNumberOfGuestsText(entryPerReservation)
  const unitMinDollarText = getMinDollarText(1)

  useEffect(() => {
    if (isHighlighted && (highlightedInventoryRef.current || highlightedInventoryButtonRef.current)) {
      if (highlightedInventoryRef.current) {
        scrollIntoView(highlightedInventoryRef.current, { behavior: 'smooth', block: 'center', inline: 'center' })
      } else if (highlightedInventoryButtonRef.current) {
        scrollIntoView(highlightedInventoryButtonRef.current, { behavior: 'smooth', block: 'center', inline: 'center' })
      }
      if (!isDisabled) {
        highlightedInventoryNameRef.current?.focus()
      }
    }
  }, [isHighlighted, inventoryItem.inventoryName, isDisabled])

  if (isMultiInventoryEnabled && (isHighlighted || quantity > 0) && !isDisabled) {
    return (
      <>
        <StyledInventoryContainer
          aria-label="inventory item"
          ref={highlightedInventoryRef}
          isDisabled={false}
          isHovered={isHovered}
          onMouseEnter={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: true, selectedDate }))}
          onMouseLeave={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: false, selectedDate }))}
        >
          <StyledInventoryHeader>{getHeaderText(inventoryItem, quantity)}</StyledInventoryHeader>
          <div>
            <StyledInventoryName ref={highlightedInventoryNameRef} tabIndex={-1}>
              {inventoryItem.inventoryName}
            </StyledInventoryName>
          </div>
          {inventoryDesc && (
            <Box mt="m">
              <Text textOverflow={3} color="inherit" fontSize="s">
                <HTMLContent
                  content={inventoryDesc}
                  allowedTags={['div', 'span', 'br', 'u', 'b', 'strong', 'i', 'em', 'a', 'ol', 'ul', 'li']}
                />
              </Text>
            </Box>
          )}
          {(inventoryDesc || inventoryImageUrls) && (
            <Box mt="m" mb="lm">
              <StyledMoreDetailsButton
                type="button"
                ref={moreDetailsButtonRef}
                onClick={() => {
                  setIsModalOpen(true)
                }}
              >
                More Details
              </StyledMoreDetailsButton>
            </Box>
          )}
          <HStack justifyContent="space-between" alignItems="center" mt="m">
            <QuantityStepper
              inventoryId={inventoryId}
              quantity={quantity}
              isDisabled={isDisabled}
              minQuantity={0}
              maxQuantity={maxPurchasableQuantity}
            />
            <StyledPriceContainer>{priceText}</StyledPriceContainer>
          </HStack>
          <HStack justifyContent="space-between" alignItems="flex-end">
            {shouldShowRemainingInventory && inventoryType !== REQUEST_TYPE ? (
              <StyledInventoryFooter>{inventoryEdits[inventoryId].remainingQuantity} remaining</StyledInventoryFooter>
            ) : (
              <div />
            )}
            {isFeesInPriceDisplayed && totalFeePrice ? (
              <StyledFeesText>* Includes {currencyFormatter.format(totalFeePrice)} in fees</StyledFeesText>
            ) : (
              <div />
            )}
          </HStack>
        </StyledInventoryContainer>
        <InventoryDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          inventoryName={inventoryItem.inventoryName}
          inventoryImageUrls={inventoryImageUrls}
          eventDateFormatted={eventDateFormatted}
          eventTimeFormatted={eventTimeFormatted}
          entryPerReservation={entryPerReservation}
          unitGuestsText={unitGuestsText}
          unitMinDollarText={unitMinDollarText}
          isFeesInPriceDisplayed={isFeesInPriceDisplayed}
          inventoryDesc={inventoryDesc}
          cancellationPolicy={cancellationPolicy}
          price={price}
          unitPriceFees={unitPriceFees}
          currencyCode={currencyCode}
        />
      </>
    )
  }

  if (isMultiInventoryEnabled && (!isHighlighted || isDisabled)) {
    return (
      <StyledInventoryButtonContainer
        type="button"
        onClick={() => {
          if (isDisabled) {
            return
          }
          toggleInventoryItem(selectedDate, selectedEventId, inventoryId)
          updateSvgLayerClickStatus(inventoryItem.inventoryName, selectedDate)
          onSvgLayerClick(inventoryItem.inventoryName, selectedDate)
        }}
        onMouseOver={() => {
          if (isDisabled) {
            return
          }
          dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: true, selectedDate }))
        }}
        onFocus={() => {
          if (isDisabled) {
            return
          }
          dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: true, selectedDate }))
        }}
        onMouseOut={() => {
          if (isDisabled) {
            return
          }
          dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: false, selectedDate }))
        }}
        onBlur={() => {
          if (isDisabled) {
            return
          }
          dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: false, selectedDate }))
        }}
        ref={highlightedInventoryButtonRef}
        isDisabled={isDisabled}
        isHovered={isHovered}
      >
        <StyledInventoryHeader>{getHeaderText(inventoryItem, quantity)}</StyledInventoryHeader>
        <HStack justifyContent="space-between">
          <StyledInventoryName ref={highlightedInventoryNameRef} tabIndex={-1}>
            {inventoryItem.inventoryName}
          </StyledInventoryName>
          <StyledPriceContainer>
            <div>{priceText}</div>
            {!!(isFeesInPriceDisplayed && totalFeePrice) && (
              <StyledFeesText>* Includes {currencyFormatter.format(totalFeePrice)} in fees</StyledFeesText>
            )}
          </StyledPriceContainer>
        </HStack>
      </StyledInventoryButtonContainer>
    )
  }

  if (isHighlighted) {
    return (
      <>
        <StyledInventoryContainer aria-label="inventory item" ref={highlightedInventoryRef}>
          {headerText && <StyledInventoryHeader>{headerText}</StyledInventoryHeader>}
          <HStack justifyContent="space-between">
            <StyledInventoryName ref={highlightedInventoryNameRef} tabIndex={-1}>
              {inventoryItem.inventoryName}
            </StyledInventoryName>
            <StyledPriceContainer>
              <div>{priceText}</div>
              {!!(isFeesInPriceDisplayed && totalFeePrice) && (
                <StyledFeesText>* Includes {priceFormatter.format(totalFeePrice, { code: currencyCode })} in fees</StyledFeesText>
              )}
            </StyledPriceContainer>
          </HStack>
          {inventoryDesc && (
            <Box mt="m">
              <Text textOverflow={3} color="inherit" fontSize="s">
                <HTMLContent
                  content={inventoryDesc}
                  allowedTags={['div', 'span', 'br', 'u', 'b', 'strong', 'i', 'em', 'a', 'ol', 'ul', 'li']}
                />
              </Text>
            </Box>
          )}
          {(inventoryDesc || inventoryImageUrls) && (
            <Box mt="m">
              <StyledMoreDetailsButton
                type="button"
                ref={moreDetailsButtonRef}
                onClick={() => {
                  setIsModalOpen(true)
                }}
              >
                More Details
              </StyledMoreDetailsButton>
            </Box>
          )}
          <HStack justifyContent="space-between" alignItems="center" mt="m">
            {maxPurchasableQuantity > 1 ? (
              <QuantityStepper
                inventoryId={inventoryId}
                quantity={quantity}
                minQuantity={inventoryItem.minQuantity}
                maxQuantity={maxPurchasableQuantity}
              />
            ) : (
              // empty div so that {justifyContent: space-between} will align the Continue btn to the right
              <div />
            )}
            <StyledButton type="button" aria-label="select inventory" onClick={selectInventoryItem.bind(null, inventoryId, availabilityId)}>
              Continue
            </StyledButton>
          </HStack>
          {shouldShowRemainingInventory && maxPurchasableQuantity > 1 && (
            <StyledInventoryFooter>{inventoryEdits[inventoryId].remainingQuantity} remaining</StyledInventoryFooter>
          )}
        </StyledInventoryContainer>
        <InventoryDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          inventoryName={inventoryItem.inventoryName}
          inventoryImageUrls={inventoryImageUrls}
          eventDateFormatted={eventDateFormatted}
          eventTimeFormatted={eventTimeFormatted}
          entryPerReservation={entryPerReservation}
          unitGuestsText={unitGuestsText}
          unitMinDollarText={unitMinDollarText}
          isFeesInPriceDisplayed={isFeesInPriceDisplayed}
          inventoryDesc={inventoryDesc}
          cancellationPolicy={cancellationPolicy}
          price={price}
          unitPriceFees={unitPriceFees}
          currencyCode={currencyCode}
        />
      </>
    )
  }

  return (
    <StyledInventoryButtonContainer
      isHovered={isHovered}
      type="button"
      onClick={() => {
        toggleInventoryItem(selectedDate, selectedEventId, inventoryId)
        updateSvgLayerClickStatus(inventoryItem.inventoryName, selectedDate)
        onSvgLayerClick(inventoryItem.inventoryName, selectedDate)
      }}
      onMouseOver={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: true, selectedDate }))}
      onFocus={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: true, selectedDate }))}
      onMouseOut={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: false, selectedDate }))}
      onBlur={() => dispatch(handleHover({ inventoryName: inventoryItem.inventoryName, hover: false, selectedDate }))}
    >
      {headerText && <StyledInventoryHeader>{headerText}</StyledInventoryHeader>}
      <HStack justifyContent="space-between">
        <StyledInventoryName>{inventoryItem.inventoryName}</StyledInventoryName>
        <StyledPriceContainer>
          <div>{priceText}</div>
          {!!(isFeesInPriceDisplayed && totalFeePrice) && (
            <StyledFeesText>* Includes {priceFormatter.format(totalFeePrice, { code: currencyCode })} in fees</StyledFeesText>
          )}
        </StyledPriceContainer>
      </HStack>
    </StyledInventoryButtonContainer>
  )
}

interface State {
  app: {
    language: string
  }
  venueInfo: {
    venueDefaultServiceCharge: number
    currencyCode: string
  }
  widgetSettings: {
    isFeesInPriceDisplayed: string
    textMinimumSpend: string
  }
  userSelection: Map<string, string>
  interactiveFloorplan: {
    svgInventory: { [key: string]: Array<{ inventoryName: string; status: string }> }
  }
  entities: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events: Map<string, Map<string, any>>
  }
}

const mapInventoryStateToProps = (
  state: State,
  { inventoryEdits, inventoryItem }: Pick<InventoryProps, 'inventoryEdits' | 'inventoryItem'>
) => {
  const {
    venueInfo: { venueDefaultServiceCharge, currencyCode },
    widgetSettings: { isFeesInPriceDisplayed, textMinimumSpend },
    userSelection,
    interactiveFloorplan: { svgInventory },
    app: { language: languageCode },
  } = state

  const selectedDate = userSelection.get('date')
  const selectedEventId = userSelection.get('eventId')
  const { inventoryId, inventoryName: invName } = inventoryItem
  const { availabilityId } = inventoryEdits[inventoryId]
  const event = state.entities.events.get(selectedEventId)
  const inventories = event.get('inventories').toJS()
  const inventory = inventories.find(({ id }: { id: string }) => id === inventoryId)
  const eventStartTime = event.get('eventStartTime')
  const eventEndTime = event.get('eventEndTime')
  const cancellationPolicy = getBookingPolicy(state, inventoryId)
  const inventoryImageUrls = Array.isArray(inventory?.inventory_images)
    ? // eslint-disable-next-line camelcase
      inventory.inventory_images.map((image: { crop_url: string }) => `/.h/download/${image.crop_url}`)
    : undefined

  return {
    availabilityId,
    currencyCode,
    isFeesInPriceDisplayed: parseBoolean(isFeesInPriceDisplayed),
    defaultServiceCharge: venueDefaultServiceCharge,
    selectedDate,
    selectedEventId,
    textMinimumSpend,
    isHovered: svgInventory[selectedDate]?.some(({ inventoryName, status }) => status === 'hover' && inventoryName === invName) || false,
    inventoryDesc: inventory?.inventory_description,
    inventoryImageUrls,
    eventDateFormatted: DateTime.from(selectedDate).toDateOnly().formatNYearSMonthNDay(),
    eventTimeFormatted:
      eventStartTime && eventEndTime ? `${TimeOnly.from(eventStartTime).formatSTime()} - ${TimeOnly.from(eventEndTime).formatSTime()}` : '',
    cancellationPolicy: cancellationPolicy?.policy,
    shouldShowRemainingInventory: event.get('shouldShowRemainingInventory'),
    isMultiInventoryEnabled: isMultiInventoryEnabled(state),
    currencyFormatter: new Intl.NumberFormat(languageCode, { style: 'currency', currency: currencyCode }),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  toggleInventoryItem: (date: string, eventId: string, inventoryId: string) => {
    dispatch(toggleInventoryItem(date, eventId, inventoryId))
  },
  updateSvgLayerClickStatus: (inventoryName: string, selectedDate: string) => {
    dispatch(handleClick({ inventoryName, selectedDate }))
  },
  selectInventoryItem: (inventoryId: string, availabilityId: string) => {
    dispatch(selectInventoryItems([{ inventoryId, availabilityId }]))
  },
})

export default connect(mapInventoryStateToProps, mapDispatchToProps)(Inventory)

const {
  color_primary: colorPrimary,
  color_widget_background: colorWidgetBackground,
  color_widget_background_opacity: colorWidgetBackgroundOpacity,
  color_cell_background: colorCellBackground,
  color_cell_background_opacity: colorCellBackgroundOpacity,
  color_hover_outline: colorHoverOutline,
  color_hover_outline_opacity: colorHoverOutlineOpacity,
  fonts_color_button: fontsColorButton,
  fonts_color_primary: fontsColorPrimary,
  fonts_color_secondary: fontsColorSecondary,
  fonts_color_links: fontsColorLinks,
} = window.widgetInit.settings

const rgbaWidgetBackground = hexToRgb(colorWidgetBackground, colorWidgetBackgroundOpacity)
const rgbaCellBackground = hexToRgb(colorCellBackground, colorCellBackgroundOpacity)
const rgbaHoverOutline = hexToRgb(colorHoverOutline, colorHoverOutlineOpacity)

const StyledInventoryButtonContainer = styled.button<{ isDisabled?: boolean; isHovered?: boolean }>`
  font-size: 14px;
  display: block;
  width: 100%;
  text-align: left;
  padding: 28px 16px;
  color: ${fontsColorPrimary};
  background-color: ${props => {
    if (props.isHovered) {
      return rgbaHoverOutline
    }
    if (props.isDisabled) {
      return rgbaWidgetBackground
    }
    return rgbaCellBackground
  }};
  appearance: none;
  border: none;
  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.isDisabled ? 0.3 : 1)};
`

const StyledInventoryContainer = styled.div<{ isDisabled?: boolean; isHovered?: boolean }>`
  font-size: 14px;
  text-align: left;
  padding: 28px 16px;
  color: ${fontsColorPrimary};
  background-color: ${props => {
    if (props.isHovered) {
      return rgbaHoverOutline
    }
    return rgbaCellBackground
  }};
  opacity: ${props => (props.isDisabled ? 0.3 : 1)};
`

const StyledInventoryHeader = styled.div`
  height: 20px;
  font-size: 12px;
  font-weight: 300;
  color: ${fontsColorSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledInventoryFooter = styled.div`
  font-size: 12px;
  font-weight: 300;
  font-style: italic;
  color: ${fontsColorSecondary};
  margin-top: 8px;
`

const StyledInventoryName = styled.div`
  font-size: 18px;
  font-weight: 500;
  padding-right: 24px;
`

const StyledPriceContainer = styled.div`
  font-size: 16px;
  font-weight: 500;
  text-align: right;
`
const StyledFeesText = styled.div`
  font-size: 12px;
  font-weight: 300;
  color: ${fontsColorSecondary};
  margin-top: 6px;
`

const StyledButton = styled(BasicButton)`
  height: 40px;
  padding: 0 28px;
  border-radius: 4px;
  background-color: ${colorPrimary};
  font-weight: 300;
  font-size: 16px;
  color: ${fontsColorButton};
  user-select: none;
  :focus-visible {
    opacity: 0.8;
    outline: 5px auto Highlight;
    outline: -webkit-focus-ring-color auto 1px;
  }
`

const StyledMoreDetailsButton = styled(BasicButton)`
  color: ${fontsColorLinks};
  user-select: none;
  :hover,
  :focus {
    text-decoration: underline !important; // BasicButton has important flag that we need to override :(
  }
  :focus-visible {
    opacity: 0.8;
    outline: 5px auto Highlight;
    outline: -webkit-focus-ring-color auto 1px;
  }
`

export const StyledImage = styled.img`
  object-fit: cover;
  object-position: center center;
  width: 100%;
  max-height: 270px;
`
interface InventoryDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  inventoryName: string
  inventoryImageUrls?: string[]
  eventDateFormatted: string
  eventTimeFormatted?: string
  entryPerReservation: number
  unitGuestsText: string
  unitMinDollarText: string
  isFeesInPriceDisplayed?: boolean
  inventoryDesc?: string
  cancellationPolicy?: string
  price?: number
  unitPriceFees: number
  currencyCode: string
}

function InventoryDetailsModal({
  isOpen,
  onClose,
  inventoryName,
  inventoryImageUrls,
  eventDateFormatted,
  eventTimeFormatted,
  entryPerReservation,
  unitGuestsText,
  unitMinDollarText,
  isFeesInPriceDisplayed,
  inventoryDesc,
  cancellationPolicy,
  price,
  unitPriceFees,
  currencyCode,
}: InventoryDetailsModalProps) {
  if (isOpen) {
    return (
      <DetailsModal title={inventoryName} modalAriaLabel={`Details for ${inventoryName}`} imageUrls={inventoryImageUrls} onClose={onClose}>
        <>
          <HStack spacing="s" mt="m" flexWrap="wrap">
            <div>{eventDateFormatted}</div>
            {eventTimeFormatted && (
              <>
                <div>•</div>
                <div>{eventTimeFormatted}</div>
              </>
            )}
          </HStack>
          <HStack spacing="s" flexWrap="wrap">
            {entryPerReservation > 1 && !!unitGuestsText && <div>{unitGuestsText}</div>}
            {unitMinDollarText && (
              <>
                {entryPerReservation > 1 && !!unitGuestsText && <div>•</div>}
                <div>{unitMinDollarText}</div>
              </>
            )}
          </HStack>
          {!!price && (
            <Box mt="m">
              <Text textStyle="h2">
                {priceFormatter.format(isFeesInPriceDisplayed ? price + unitPriceFees : price, {
                  code: currencyCode,
                })}
              </Text>
              {isFeesInPriceDisplayed && !!unitPriceFees && (
                <>
                  &nbsp;
                  <Text textStyle="body2">* Includes {priceFormatter.format(unitPriceFees, { code: currencyCode })} in fees</Text>
                </>
              )}
            </Box>
          )}
          <DividerLine margin="none" mt="m" mb="m" />
          {inventoryDesc && (
            <>
              <div>
                <Text textStyle="h3">About</Text>
              </div>
              <Box mt="m" mb="lm">
                <HTMLContent
                  content={inventoryDesc}
                  allowedTags={['div', 'span', 'br', 'u', 'b', 'strong', 'i', 'em', 'a', 'ol', 'ul', 'li']}
                />
              </Box>
            </>
          )}
          {cancellationPolicy && (
            <>
              <div>
                <Text textStyle="h3">Cancellation Policy</Text>
              </div>
              <Box mt="m" mb="lm">
                {cancellationPolicy}
              </Box>
            </>
          )}
        </>
      </DetailsModal>
    )
  }
  return null
}
